import { minutesToDuration } from '../../../../lib/DateTime.js';
import { useEffect, useRef, useState } from 'react';
import SelectionBlock from '../components/SelectionBlock.jsx';
import ErrorMessage from '../../../ErrorMessage';
import { useEventTypesQuery } from '../../../../lib/SharedQueries.js';
import { formatCurrency } from '~/lib/CurrencyHelpers';

export function useQuestionEventTypes(bookableResourceId, settings) {
    const { isLoading, data } = useEventTypesQuery(
        bookableResourceId,
        settings?.bookableResources,
    );

    let types = data || [];
    if (!types.filter) {
        return [];
    }

    types = types.filter(type => {
        // If we're selecting a bookableResource first and the event types requires 2 people then omit it...
        if (
            type?.settings?.numberBookableResourcesRequired > 1 &&
            bookableResourceId > 0
        ) {
            return false;
        }

        if (settings?.eventTypes && settings?.eventTypes.length > 0) {
            return settings.eventTypes.indexOf(type.id) > -1;
        } else {
            return true;
        }
    });

    return {
        isLoading,
        types,
    };
}

const SelectEventType = ({
    value,
    onChange,
    onChangeCapacity,
    bookableResourceId,
    settings,
    selectedRightContent,
}) => {
    const [selectedCategory, setSelectedCategory] = useState(null);

    const { isLoading, types } = useQuestionEventTypes(
        bookableResourceId,
        settings,
    );

    const allTypeOptions = types.map(type => ({
        title: type.name,
        image: !settings?.compact && type.image,
        category: type.category?.name,
        subtitle: settings.hideDuration
            ? ''
            : minutesToDuration(type.duration) +
              (type.is_series ? ' per class' : ''),
        description: !settings?.compact && type.description,
        price: settings.hidePrice ? '' : type.price,
        discountedPrice: type.discounted_price,
        capacity: type.settings?.max_attendees || 1,
        value: type.id,
    }));

    const allOptionsHaveCategory =
        allTypeOptions.filter(type => !type.category).length === 0;
    const categories = [...new Set(allTypeOptions.map(type => type.category))];
    const hasCategoryStep =
        allOptionsHaveCategory &&
        categories.length >= 2 &&
        allTypeOptions.length > 4;

    let categoryOptions = [];
    categories.forEach(category =>
        categoryOptions.push({ title: category, value: category }),
    );

    let typeOptions = allTypeOptions;
    if (hasCategoryStep && selectedCategory) {
        typeOptions = allTypeOptions.filter(
            type => type.category === selectedCategory,
        );
    }

    function setType(type) {
        if (parseInt(type?.value) !== parseInt(value)) {
            onChange(type?.value);
        }

        onChangeCapacity(type?.capacity);
    }

    useEffect(() => {
        if (!!types && value && hasCategoryStep && !selectedCategory) {
            // Make sure category is selected if we're direct linking to something in a category
            const typeOption = typeOptions.find(to => to.value === value);
            if (typeOption) {
                setSelectedCategory(typeOption?.category);
            }
        }
    }, [value, types]);

    useEffect(() => {
        if (typeOptions.length === 1 && typeOptions[0].value !== value) {
            setType(typeOptions[0]);
        }
    }, [JSON.stringify(typeOptions), value]);

    if (
        (settings?.hideEventType ||
            (allTypeOptions.length === 1 && typeOptions[0].value === value)) &&
        !selectedRightContent
    ) {
        return null;
    }

    return (
        <div className="mt-4">
            {isLoading && <div>Loading...</div>}

            {hasCategoryStep && (
                <>
                    <div className="panel-label">Choose Type</div>
                    <SelectionBlock
                        value={selectedCategory}
                        onChange={value => {
                            if (
                                selectedCategory &&
                                selectedCategory !== value
                            ) {
                                // we're changing categories, reset the value
                                setType(null);
                            }
                            setSelectedCategory(value);
                        }}
                        options={categoryOptions}
                    />
                </>
            )}

            {(!hasCategoryStep || selectedCategory) && (
                <>
                    <SelectionBlock
                        value={value}
                        hideCategoryTitle={hasCategoryStep}
                        onChange={value =>
                            setType(typeOptions.find(to => to.value === value))
                        }
                        options={typeOptions}
                        selectedRightContent={selectedRightContent}
                    />
                </>
            )}
            {typeOptions.length === 0 && !isLoading && (
                <ErrorMessage>
                    There are no types available that can be booked.
                </ErrorMessage>
            )}
        </div>
    );
};

export default SelectEventType;
